import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCoins, FaChartPie, FaDice, FaArrowRight, FaPlay, FaInfoCircle, FaCreditCard, FaBitcoin, FaEthereum, FaAngleDoubleRight, FaGamepad, FaBomb, FaSync, FaBoxOpen, FaSyncAlt } from 'react-icons/fa';
import { SiTether, SiLitecoin, SiSolana } from 'react-icons/si';
import { GiCrossedSwords } from 'react-icons/gi';
import { motion, AnimatePresence } from 'framer-motion';
import { UserContext } from './App';
import { ChatContext } from './ChatContext';

const GAMES = [
  { key: 'cases', name: 'Cases', subtitle: 'Solo open cases', route: '/cases', Icon: FaBoxOpen, image: '/cases.png' },
  { key: 'battles', name: 'Case Battles', subtitle: 'Battle till the end', route: '/case-battles', Icon: GiCrossedSwords, image: '/casebattle.jpg' },
  { key: 'coinflip', name: 'Coinflip', subtitle: 'Flip a coin', route: '/coinflip', Icon: FaCoins, image: '/cfflip.jpg' },
  { key: 'jackpot', name: 'Jackpot', subtitle: 'Win the pot', route: '/jackpot', Icon: FaSync, image: '/jackpotjp.jpg' },
  { key: 'bust', name: 'Bust', subtitle: 'Roll the dice', route: '/bust', Icon: FaDice, image: '/bust.jpg' }
];

// Animated floating particles for the hero
const HeroParticles = () => {
  const particles = useMemo(() => 
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 2 + Math.random() * 4,
      duration: 15 + Math.random() * 20,
      delay: Math.random() * -20,
      opacity: 0.1 + Math.random() * 0.3
    })), []);

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 1 }}>
      {particles.map(p => (
        <div key={p.id} style={{
          position: 'absolute',
          left: `${p.x}%`,
          top: `${p.y}%`,
          width: p.size,
          height: p.size,
          borderRadius: '50%',
          background: 'rgba(255, 183, 3, 0.6)',
          boxShadow: `0 0 ${p.size * 3}px rgba(255, 183, 3, 0.3)`,
          opacity: p.opacity,
          animation: `heroFloat ${p.duration}s ease-in-out ${p.delay}s infinite`
        }} />
      ))}
      <style>{`
        @keyframes heroFloat {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.1; }
          25% { transform: translate(30px, -40px) scale(1.5); opacity: 0.4; }
          50% { transform: translate(-20px, -60px) scale(1); opacity: 0.2; }
          75% { transform: translate(40px, -20px) scale(1.3); opacity: 0.35; }
        }
      `}</style>
    </div>
  );
};

const GameCard = ({ g, onNavigate }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div 
            className="rc-gameCard" 
            onClick={() => onNavigate(g.route)} 
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                background: '#16191f',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.2, 0, 0, 1)',
                transform: isHovered ? 'translateY(-8px)' : 'none',
                boxShadow: isHovered ? '0 20px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(255, 183, 3, 0.2)' : '0 10px 20px rgba(0,0,0,0.2)',
                height: '290px',
                cursor: 'pointer',
                border: '1px solid rgba(255,255,255,0.03)'
            }}
        >
            <div style={{ height: '190px', width: '100%', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 40%, #16191f 100%)', zIndex: 2 }} />
                {g.image ? (
                    <img 
                        src={g.image} 
                        style={{ 
                            width: '100%', height: '100%', objectFit: 'cover',
                            transition: 'transform 0.5s cubic-bezier(0.2, 0, 0, 1)',
                            transform: isHovered ? 'scale(1.08)' : 'scale(1)',
                            filter: isHovered ? 'brightness(1.1) saturate(1.1)' : 'brightness(0.8)'
                        }} 
                        alt={g.name} 
                    />
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#0d0f13' }}>
                        <g.Icon size={48} color="#ffb703" style={{ opacity: 0.5 }} />
                    </div>
                )}
            </div>

            <div style={{ padding: '16px 20px', position: 'relative', zIndex: 3, display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center', textAlign: 'center' }}>
                <div style={{ fontSize: '17px', fontWeight: 900, color: '#fff', letterSpacing: '0.3px' }}>{g.name}</div>
                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: 700, opacity: 0.8 }}>{g.subtitle}</div>
                <div style={{
                    marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px', color: '#ffb703', fontSize: '10px', fontWeight: 950, textTransform: 'uppercase', letterSpacing: '0.5px',
                    opacity: isHovered ? 1 : 0, transform: isHovered ? 'translateY(0)' : 'translateY(10px)', transition: 'all 0.3s ease'
                }}>
                    Launch Game <FaArrowRight size={8} />
                </div>
            </div>
            <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: '#ffb703', transform: isHovered ? 'scaleX(1)' : 'scaleX(0)', transition: 'transform 0.3s ease', boxShadow: '0 0 15px rgba(255, 183, 3, 0.8)', zIndex: 4 }} />
        </div>
    );
};

const HowItWorks = () => {
    const { user, setIsDepositModalOpen, setIsAuthModalOpen, setAuthModalView } = React.useContext(UserContext);

    const handleDepositClick = () => {
        if (!user) {
            setAuthModalView('login');
            setIsAuthModalOpen(true);
        } else {
            setIsDepositModalOpen(true);
        }
    };

    const steps = [
        {
            step: "STEP 1",
            title: "Refill your balance",
            desc: "Top up your balance with various payment options or claim free bonuses"
        },
        {
            step: "STEP 2",
            title: "Play Games",
            desc: "Play thrilling game modes for a chance to increase your balance"
        },
        {
            step: "STEP 3",
            title: "Cash or Claim",
            desc: "Withdraw your winnings instantly to Bitcoin, Ethereum, Litecoin and more cryptocurrencies!"
        }
    ];

    const payMethods = [
        { icon: <FaCreditCard />, color: '#ffb703', bg: 'rgba(250, 204, 21, 0.1)' },
        { icon: <FaBitcoin />, color: '#f7931a', bg: 'rgba(247, 147, 26, 0.1)' },
        { icon: <FaEthereum />, color: '#627eea', bg: 'rgba(98, 126, 234, 0.1)' },
        { icon: <SiTether />, color: '#26a17b', bg: 'rgba(38, 161, 123, 0.1)' },
        { icon: <SiLitecoin />, color: '#345d9d', bg: 'rgba(52, 93, 157, 0.1)' },
        { icon: <SiSolana />, color: '#14f195', bg: 'rgba(20, 241, 149, 0.1)' }
    ];

    return (
        <div style={{ marginTop: '60px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '30px' }}>
                <FaInfoCircle style={{ color: '#ffb703', fontSize: '20px' }} />
                <h2 style={{ fontSize: '20px', fontWeight: 900, color: '#fff', margin: 0, textTransform: 'capitalize', letterSpacing: '0.5px' }}>
                    How RustyCoin Works
                </h2>
            </div>

            <div style={{ 
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', 
                width: '100%', flexWrap: 'wrap', marginBottom: '40px' 
            }}>
                {steps.map((s, i) => (
                    <React.Fragment key={i}>
                        <div style={{
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.2) 100%)',
                            border: '1px solid rgba(255,255,255,0.05)',
                            borderRadius: '16px',
                            padding: '30px 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            flex: 1,
                            minWidth: '250px',
                            maxWidth: '320px',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
                        }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #ffc130, #ffb703)',
                                color: '#1a1d23',
                                padding: '4px 12px',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: 950,
                                marginBottom: '20px',
                                textTransform: 'uppercase'
                            }}>
                                {s.step}
                            </div>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                                <div style={{ fontSize: '18px', fontWeight: 900, color: '#fff', marginBottom: '12px' }}>
                                    {s.title}
                                </div>
                                <div style={{ fontSize: '13px', color: '#94a3b8', lineHeight: 1.5, fontWeight: 500, padding: '0 10px' }}>
                                    {s.desc}
                                </div>
                            </div>
                        </div>
                        {i < steps.length - 1 && (
                            <div style={{ color: '#ffb703', fontSize: '24px', display: 'flex', alignItems: 'center', opacity: 0.8 }}>
                                <FaAngleDoubleRight />
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                <div style={{ fontSize: '14px', fontWeight: 800, color: '#fff' }}>
                    Select your favourite payment method
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <style>{`
                        .pay-icon-btn { transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); cursor: pointer; }
                        .pay-icon-btn:hover { transform: translateY(-6px) scale(1.15); filter: brightness(1.3); }
                    `}</style>
                    {payMethods.map((pm, idx) => (
                        <div key={idx} className="pay-icon-btn" onClick={handleDepositClick} style={{ width: '50px', height: '50px', background: pm.bg, border: `1px solid ${pm.color}40`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: pm.color, boxShadow: `0 4px 15px ${pm.bg}` }}>
                            {pm.icon}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const LiveBetsTable = () => {
    const { socket } = React.useContext(ChatContext);
    const [bets, setBets] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch('/api/wager/history?limit=15')
            .then(res => res.json())
            .then(data => {
                if (data.wagers) setBets(data.wagers);
                setLoading(false);
            })
            .catch(err => { console.error(err); setLoading(false); });

        if (!socket) return;
        const handleNewWager = (wager) => { setBets(prev => [wager, ...prev].slice(0, 15)); };
        socket.on('new_wager', handleNewWager);
        return () => socket.off('new_wager', handleNewWager);
    }, [socket]);

    return (
        <div style={{ 
            width: '100%', 
            marginTop: '60px', 
            background: 'rgba(22, 25, 31, 0.4)', 
            borderRadius: '16px', 
            padding: '24px',
            border: '1px solid rgba(255,255,255,0.02)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px', justifyContent: 'center' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 10px #22c55e' }} />
                <h3 style={{ color: '#fff', margin: 0, fontSize: '18px', fontWeight: 900, letterSpacing: '0.5px' }}>Live Bets</h3>
            </div>
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1.5fr 1fr 0.8fr 1.2fr', 
                padding: '0 20px 12px 20px', 
                color: '#4b5563', 
                fontSize: '11px', 
                fontWeight: 800, 
                textTransform: 'uppercase', 
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                letterSpacing: '1px'
            }}>
                <div>Game</div><div>User</div><div style={{ textAlign: 'center' }}>Wager</div><div style={{ textAlign: 'center' }}>Multi</div><div style={{ textAlign: 'right' }}>Payout</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px' }}>
                <AnimatePresence initial={false}>
                    {bets.map((bet, idx) => {
                        const isWin = bet.profit > 0;
                        const GameIcon = { 'coinflip': FaCoins, 'jackpot': FaSyncAlt, 'mines': FaBomb, 'bust': FaDice, 'cases': FaBoxOpen, 'case_battles': GiCrossedSwords, 'limbo': FaChartPie }[bet.gameType] || FaGamepad;
                        return (
                            <motion.div 
                                key={bet._id || idx} 
                                initial={{ opacity: 0, x: -10 }} 
                                animate={{ opacity: 1, x: 0 }} 
                                exit={{ opacity: 0, x: 10 }}
                                style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: '1fr 1.5fr 1fr 0.8fr 1.2fr', 
                                    padding: '14px 20px', 
                                    background: isWin ? 'rgba(34, 197, 94, 0.05)' : 'rgba(255,255,255,0.01)', 
                                    borderRadius: '8px', 
                                    alignItems: 'center', 
                                    borderLeft: isWin ? '2px solid #22c55e' : '2px solid transparent',
                                    transition: 'background 0.2s ease'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#fff', fontSize: '13px', fontWeight: 800 }}>
                                    <GameIcon size={14} color="#ffb703" /> 
                                    <span style={{ textTransform: 'capitalize' }}>{bet.gameType?.replace('_', ' ')}</span>
                                </div>
                                <div style={{ color: '#fff', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{ background: 'rgba(255, 183, 3, 0.1)', color: '#ffb703', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 900 }}>{bet.userId?.level || 1}</div>
                                    <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bet.userId?.username || 'Guest'}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', color: '#ffb703', fontWeight: 900, fontSize: '13px' }}>
                                    <img src="/scrap.webp" style={{ width: '14px' }} alt="scrap" /> 
                                    {bet.amount?.toFixed(2)}
                                </div>
                                <div style={{ textAlign: 'center', color: isWin ? '#22c55e' : '#4b5563', fontWeight: 900, fontSize: '13px' }}>
                                    x{((bet.profit + bet.amount) / (bet.amount || 1)).toFixed(2)}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', color: isWin ? '#22c55e' : '#4b5563', fontWeight: 900, fontSize: '13px' }}>
                                    {isWin && <img src="/scrap.webp" style={{ width: '14px', filter: 'hue-rotate(90deg) brightness(1.2)' }} alt="win" />} 
                                    {(bet.profit + bet.amount)?.toFixed(2)}
                                </div>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default function FrontpageMain({ layoutSearchTerm = '' }) {
    const navigate = useNavigate();
    const { user } = React.useContext(UserContext);
    const [currentSlide, setCurrentSlide] = useState(0);

    const slides = [
        {
            title: <>NEW VIP<br/><span style={{ color: '#fff' }}>REWARD SYSTEM</span></>,
            desc: 'Feel like a VIP with our new revamped reward system.',
            bg: 'linear-gradient(135deg, #ffb703 0%, #d97706 100%)',
            icon1: 'VIP', icon2: 'W'
        },
        {
            title: <>CLAIM YOUR<br/><span style={{ color: '#4ade80' }}>3 FREE CASES</span></>,
            desc: 'No deposit. No catch. Keep whatever you pull.',
            bg: 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)',
            icon1: 'FREE', icon2: 'CASE'
        },
        {
            title: <>CHECK OUT<br/><span style={{ color: '#ffb703' }}>OUR SOCIALS</span></>,
            desc: 'Stay up to date on giveaways, events and more!',
            bg: 'linear-gradient(135deg, #1a1d23 0%, #ffb703 100%)',
            icon1: 'SOCIAL', icon2: 'DISCORD'
        }
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide(s => (s + 1) % slides.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [slides.length]);

    const filtered = useMemo(() => {
        const term = (layoutSearchTerm || '').trim().toLowerCase();
        return term ? GAMES.filter(g => g.name.toLowerCase().includes(term)) : GAMES;
    }, [layoutSearchTerm]);

    return (
        <div className="rc-frontpage" style={{ padding: '0 20px 40px 20px' }}>
            <HeroParticles />
            {/* ═══════════ PREMIUM HERO ═══════════ */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '32px', minHeight: '260px', width: '100%' }}>
                
                {/* Left Box: Welcome Back */}
                <div style={{ 
                    flex: '0 0 45%',
                    background: '#16191f',
                    borderRadius: '16px',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '6px',
                    padding: '32px',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    border: '1px solid rgba(255,255,255,0.02)'
                }}>
                    <div style={{ position: 'relative', zIndex: 2 }}>
                        <div style={{ fontSize: '24px', fontWeight: 900, color: '#ffb703', marginBottom: '4px' }}>
                            Welcome back,
                        </div>
                        <div style={{ fontSize: '28px', fontWeight: 900, color: '#fff', marginBottom: '32px' }}>
                            {user?.username || 'big cat'}
                        </div>

                        <div style={{ color: '#64748b', fontSize: '13px', fontWeight: 700, marginBottom: '16px' }}>
                            Level progression
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '40px', maxWidth: '240px' }}>
                            <div style={{ 
                                background: '#2d3139', padding: '4px 10px', borderRadius: '4px', 
                                fontSize: '12px', fontWeight: 900, color: '#fff', fontStyle: 'italic' 
                            }}>
                                {user?.level?.current || 0}
                            </div>
                            <div style={{ flex: 1, height: '2px', background: '#2d3139', position: 'relative' }}>
                                <div style={{ 
                                    position: 'absolute', left: `${user?.level?.progress || 0}%`, top: '-4px', 
                                    transform: 'translateX(-50%)', width: '0', height: '0', 
                                    borderLeft: '5px solid transparent', borderRight: '5px solid transparent', 
                                    borderTop: '6px solid #ffb703' 
                                }} />
                                <div style={{ width: `${user?.level?.progress || 0}%`, height: '100%', background: '#ffb703' }} />
                            </div>
                            <div style={{ 
                                background: '#2d3139', padding: '4px 10px', borderRadius: '4px', 
                                fontSize: '12px', fontWeight: 900, color: '#fff', fontStyle: 'italic' 
                            }}>
                                {(user?.level?.current || 0) + 1}
                            </div>
                        </div>

                        <button 
                            onClick={() => navigate('/dashboard?tab=rewards')}
                            style={{
                                background: '#ffb703',
                                border: 'none', padding: '12px 24px', borderRadius: '4px',
                                color: '#000', fontSize: '13px', fontWeight: 900,
                                cursor: 'pointer', transition: 'all 0.2s ease',
                                display: 'inline-block'
                            }}
                        >
                            Explore Rewards
                        </button>
                    </div>

                    {/* Character/Gold Image added per user request */}
                    <img 
                        src="/gold.png" 
                        alt="" 
                        style={{ 
                            position: 'absolute', 
                            right: '-30px', 
                            bottom: '-40px', 
                            height: '120%', 
                            objectFit: 'contain', 
                            zIndex: 1,
                            filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.5))'
                        }} 
                    />
                </div>

                {/* Right Box: Sliding Promotional Banners */}
                <div style={{
                    flex: 1,
                    background: slides[currentSlide].bg,
                    borderRadius: '16px',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: '40px',
                    transition: 'background 0.5s ease'
                }}>
                    <AnimatePresence mode="wait">
                        <motion.div 
                            key={currentSlide}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                            style={{ position: 'relative', zIndex: 2 }}
                        >
                            <h2 style={{ 
                                fontSize: '42px', fontWeight: 900, color: '#fff', 
                                lineHeight: 1, margin: '0 0 8px 0', fontStyle: 'italic', letterSpacing: '-1px',
                                textShadow: '2px 2px 0px rgba(0,0,0,0.1)'
                            }}>
                                {slides[currentSlide].title}
                            </h2>
                            <p style={{ 
                                color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 600, maxWidth: '280px',
                                lineHeight: 1.4, fontStyle: 'italic'
                            }}>
                                {slides[currentSlide].desc}
                            </p>
                        </motion.div>
                    </AnimatePresence>
                    
                    {/* Abstract visuals in background */}
                    <div style={{ position: 'absolute', right: '5%', top: '50%', transform: 'translateY(-50%) rotate(15deg)', fontSize: '120px', fontWeight: 900, color: '#fff', opacity: 0.1, fontStyle: 'italic', lineHeight: 0.8, transition: 'all 0.5s' }}>
                        {slides[currentSlide].icon1}
                    </div>
                    <div style={{ position: 'absolute', right: '35%', top: '-20%', fontSize: '120px', fontWeight: 900, color: '#fff', opacity: 0.05, fontStyle: 'italic', lineHeight: 0.8, transition: 'all 0.5s' }}>
                        {slides[currentSlide].icon2}
                    </div>
                    
                    {/* Slide dots */}
                    <div style={{ position: 'absolute', bottom: '20px', left: '0', right: '0', display: 'flex', justifyContent: 'center', gap: '8px', zIndex: 3 }}>
                        {slides.map((_, i) => (
                            <div key={i} 
                                onClick={() => setCurrentSlide(i)}
                                style={{ 
                                    width: currentSlide === i ? '24px' : '8px', 
                                    height: '4px', 
                                    background: currentSlide === i ? '#fff' : 'rgba(255,255,255,0.3)', 
                                    borderRadius: '2px',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer'
                                }} 
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px', color: '#fff', fontSize: '20px', fontWeight: 900, justifyContent: 'center' }}>
                <div style={{ background: '#ffb703', padding: '6px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 15px rgba(255, 183, 3, 0.3)' }}>
                    <FaGamepad size={18} color="#000" />
                </div>
                House Originals
            </div>

            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(5, 1fr)', 
                gap: '20px', 
                marginBottom: '80px',
                justifyContent: 'center'
            }}>
                {filtered.map(g => <GameCard key={g.key} g={g} onNavigate={navigate} />)}
            </div>

            <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
                <LiveBetsTable />
                <div style={{ height: '60px' }} />
                <HowItWorks />
            </div>
        </div>
    );
}
